import MandatoryIcon from 'components/icons/MandatoryIcon';
import { MAX_FILE_SIZE, MAX_IMG_SIZE, ProcessStatus, Role } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import Tick from 'images/tick.svg';
import { WizardTabModel } from 'models/tabModels';
import WizardStep1 from 'pages/wizard/WizardStep1';
import WizardStep2 from 'pages/wizard/WizardStep2';
import WizardStep3 from 'pages/wizard/WizardStep3';
import WizardStep4 from 'pages/wizard/WizardStep4';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EpdLinks } from 'routes/EpdRoutes';
import { ActionState, CollectionModel, EPDModel, EPDVerifierModel, MembershipModel, PCRModel } from 'services/EpdClient';
import EpdService from 'services/EpdService';
import MembershipService from 'services/MembershipService';
import styled, { css } from 'styled-components';
import { Container, ErrorText, TextSmall } from 'styles/Styles.styled';
import Spinner from 'styles/spinner.styled';
import StatusBox from './StatusBox';
import ToolButtons from './ToolButtons';
import {
  step1ValidationSchema,
  step2ValidationSchema,
  step3ValidationSchema,
  step4ValidationSchema,
  isValidIgnoringWarnings,
} from './Validation';
import { StepStatus } from './types';
import CollectionService from 'services/CollectionService';
import { QUERY_KEYS } from 'services/api/constants';
import { getApiErrorStatus } from 'util/apiErrorUtils';
import { getEpdWriteBlockedMessage } from 'util/epdActionErrorUtils';
import { useQueryClient } from '@tanstack/react-query';

const EpdWizard: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [epd, setEpd] = useState<EPDModel>();
  const [updatingEpd, setUpdatingEpd] = useState<ProcessStatus>(ProcessStatus.None);
  // The server's message for a refused save or load (409 company locked / Read-only EPD, 404 Hidden EPD).
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const { id } = useParams<any>();
  const { companyId, changeCompany } = React.useContext(CompanyContext);
  const [agreementConsent, setAgreementConsent] = useState<boolean>(false);
  const history = useHistory();
  const [verifiers, setVerifiers] = useState<EPDVerifierModel[]>([]);
  const [epdDevelopers, setEpdDevelopers] = useState<MembershipModel[]>([]);
  const [isLoadingVerifiers, setIsLoadingVerifiers] = useState(false);
  const [collectionList, setCollectionList] = useState<CollectionModel[]>([]);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const queryClient = useQueryClient();

  // A 404 means this copy has been Hidden since the page was opened. Re-ask which copy is visible, as
  // Dynamic Forms does: WizardSelector then opens the visible copy, or says "EPD not found" and goes to the dashboard.
  const handleServerError = (error: unknown) => {
    setServerErrorMessage(getEpdWriteBlockedMessage(error));
    if (getApiErrorStatus(error) === 404) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EPD_VISIBLE_COPY, id] });
    }
  };

  const goToStep = (stepNumber: number) => setActiveStep(stepNumber);

  const onChangeServiceAgreementsStatus = (val: boolean) => {
    onChangeEpd('ServiceAgreementStatus', val);
    setAgreementConsent(val);
  };

  const onChangeEpd = async (propertyName: string, val: any, onlyFetch: boolean = false) => {
    if (!epd || !epd.id) {
      return;
    }

    if (onlyFetch) {
      await fetchEpd();
      return;
    }

    await addEpdProperty(epd.id, propertyName, val);
  };

  const addEpdProperty = async (epdId: string, propertyName: string, val: any) => {
    try {
      setUpdatingEpd(ProcessStatus.Fetching);
      const result = await EpdService.updateProperty(epdId, propertyName, val === undefined ? null : val);
      setEpd(result);
      setUpdatingEpd(ProcessStatus.Success);
    } catch (error) {
      handleServerError(error);
      setUpdatingEpd(ProcessStatus.Error);
    }
  };

  const onRemoveFile = async (fileId: string) => {
    if (!epd?.versionId) {
      return;
    }

    try {
      setUpdatingEpd(ProcessStatus.Fetching);
      const result = await EpdService.removeEpdFile(fileId, epd.versionId);
      setEpd(result);
      setUpdatingEpd(ProcessStatus.Success);
    } catch (error) {
      handleServerError(error);
      setUpdatingEpd(ProcessStatus.Error);
    }
  };

  const addEpdFile = async (
    propertyName: string,
    file: File,
    documentTypeId: string | null,
    documentLanguageId: string | null
  ) => {
    if (!epd?.id) {
      return;
    }

    if (propertyName === 'ProductImage' && file.size > MAX_IMG_SIZE) {
      toast.error(
        t('epdWizard.messages.errorTooLargeImageFile', { size: Math.floor(MAX_IMG_SIZE / (1024 * 1024)) }) as string,
        {
          position: 'top-center',
        }
      );
      return;
    }

    const fileName = file.name;
    const fileType = file.type;
    const reader = new FileReader();
    let resolveUploading: (value?: undefined) => void;
    let rejectUploading: (reason?: any) => void;
    const uploadingPromise = new Promise((resolve, reject) => {
      resolveUploading = resolve;
      rejectUploading = reject;
    });
    reader.onload = async () => {
      const fileBlob = reader.result as ArrayBuffer;
      if (!fileBlob) {
        rejectUploading(new Error('No file blob after reading file.'));
        return;
      }

      const blob = new Blob([new Uint8Array(fileBlob)], { type: fileType });
      if (blob.size > MAX_FILE_SIZE) {
        toast.error(t('epdWizard.messages.errorTooLargeFile') as string, {
          position: 'top-center',
        });
        rejectUploading(new Error('File is too large.'));
        return;
      }

      try {
        setUpdatingEpd(ProcessStatus.Fetching);
        const result = await EpdService.addEpdFile(
          epd.id!,
          propertyName,
          fileName,
          blob,
          documentTypeId,
          documentLanguageId
        );
        setEpd(result);
        setUpdatingEpd(ProcessStatus.Success);
      } catch (e) {
        handleServerError(e);
        setUpdatingEpd(ProcessStatus.Error);
        rejectUploading(e);
        return;
      }

      resolveUploading();
    };
    reader.readAsArrayBuffer(file);

    await uploadingPromise;
  };

  const fetchEpd = async () => {
    if (!id) {
      return;
    }

    try {
      setStatus(ProcessStatus.Fetching);
      const resultEpd = await EpdService.getEpd(id);
      setEpd(resultEpd);
      setAgreementConsent(resultEpd?.serviceAgreementStatus ?? false);
      setStatus(ProcessStatus.Success);
    } catch (error) {
      handleServerError(error);
      setStatus(ProcessStatus.Error);
    }
  };

  const fetchCollections = async () => {
    if (!companyId) return;
    setIsLoadingCollections(true);
    try {
      const res = await CollectionService.getCollections(companyId);
      setCollectionList(res ?? []);
    } finally {
      setIsLoadingCollections(false);
    }
  };

  const fetchVerifierUsers = async () => {
    if (!!companyId) {
      setIsLoadingVerifiers(true);
      const res = await MembershipService.getVerifiers(companyId);
      setVerifiers(res);
      setIsLoadingVerifiers(false);
    }
  };

  const fetchPractitioners = async () => {
    if (!!companyId) {
      const practitioners = await MembershipService.getMembershipsByCompanyAndRole(companyId!, Role.EPDDeveloper);
      const owners = await MembershipService.getMembershipsByCompanyAndRole(companyId!, Role.EPDOwner);
      setEpdDevelopers([...practitioners, ...owners]);
    }
  };

  useEffect(() => {
    if (!!companyId) {
      fetchVerifierUsers();
      fetchPractitioners();
      fetchCollections();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!!companyId) {
      fetchEpd();
    }
  }, [id, companyId, changeCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  // Steps also drive updatingEpd themselves; a new save must not show the message of an earlier one.
  useEffect(() => {
    if (updatingEpd === ProcessStatus.Fetching) {
      setServerErrorMessage(null);
    }
  }, [updatingEpd]);

  if (status === ProcessStatus.Fetching) {
    return (
      <Container>
        <div>{t('epdWizard.messages.loading')}</div>
      </Container>
    );
  }

  if (status === ProcessStatus.Error) {
    return (
      <Container>
        <div>{serverErrorMessage ?? t('epdWizard.messages.errorOnLoading')}</div>
      </Container>
    );
  }

  if (!epd) {
    return null;
  }

  const updateEpd = (state: {} | null) => {
    if (!state) {
      history.push(EpdLinks.dashboard());
      return;
    }
    fetchEpd();
  };

  const changePcr = (pcr: PCRModel) => {
    setEpd({ ...epd, pcr: pcr });

    if (pcr?.id) {
      onChangeEpd('PCR', pcr?.id);
    }
  };

  const changeCpcr = (cpcr: PCRModel) => {
    setEpd({ ...epd, cpcr: cpcr });

    if (cpcr?.id) {
      onChangeEpd('CPCR', cpcr?.id);
    }
  };

  const removePcr = () => {
    setEpd({ ...epd, pcr: undefined });
    onChangeEpd('PCR', null);
  };

  const removeCpcr = () => {
    setEpd({ ...epd, cpcr: undefined });
    onChangeEpd('CPCR', null);
  };

  const wizardTabs: WizardTabModel<EPDModel>[] = [
    {
      name: (
        <>
          {t('epdWizard.wizardStep1.name')}
          <TextSmall>{t('epdWizard.wizardStep1.description')}</TextSmall>
        </>
      ),
      component: (
        <WizardStep1
          key={'step1'}
          validation={step1ValidationSchema(epd?.verificationType, verifiers, isLoadingVerifiers)}
          epdDevelopers={epdDevelopers}
          verifiers={verifiers}
          isLoadingVerifiers={isLoadingVerifiers}
          refetchVerifiers={fetchVerifierUsers}
          refetchPractitioners={fetchPractitioners}
        />
      ),
      isValid: (e) => isValidIgnoringWarnings(step1ValidationSchema(epd?.verificationType, verifiers), e),
    },
    {
      name: (
        <>
          {t('epdWizard.wizardStep2.name')}
          <TextSmall>{t('epdWizard.wizardStep2.description')}</TextSmall>
        </>
      ),
      component: (
        <WizardStep2
          key={'wiz2'}
          validation={step2ValidationSchema}
          setUpdatingEpd={setUpdatingEpd}
          setUpdatedEpd={setEpd}
          onUploadFile={addEpdFile}
          onRemoveFile={onRemoveFile}
          isFetching={updatingEpd === ProcessStatus.Fetching}
          collectionList={collectionList}
          isLoadingCollections={isLoadingCollections}
        />
      ),
      isValid: (e) => isValidIgnoringWarnings(step2ValidationSchema, e),
    },
    {
      name: (
        <>
          {t('epdWizard.wizardStep3.name')}
          <TextSmall>{t('epdWizard.wizardStep3.description')}</TextSmall>
        </>
      ),
      component: (
        <WizardStep3
          key="wiz3"
          validation={step3ValidationSchema}
          onChangeEpd={onChangeEpd}
          onUploadFile={addEpdFile}
          onRemoveFile={onRemoveFile}
        />
      ),
      isValid: (e) => isValidIgnoringWarnings(step3ValidationSchema, e),
    },
    {
      name: (
        <>
          {t('epdWizard.wizardStep4.name')}
          <TextSmall>{t('epdWizard.wizardStep4.description')}</TextSmall>
        </>
      ),
      component: (
        <WizardStep4 key="wiz4" registerConsent={onChangeServiceAgreementsStatus} validation={step4ValidationSchema} />
      ),
      isValid: (e) =>
        isValidIgnoringWarnings(step4ValidationSchema, e) &&
        (agreementConsent ||
          (epd.epdState?.sendForPublication == ActionState.Hidden &&
            epd.epdState?.sendForPublicationEditorial == ActionState.Hidden)),
    },
  ];

  if (!companyId) {
    return <Container>Receiving company identifier... or it is not set and you need to relogin</Container>;
  }

  return (
    <Container>
      {updatingEpd === ProcessStatus.Fetching && <Spinner />}
      {updatingEpd === ProcessStatus.Error && (
        <ErrorUpdating>{serverErrorMessage ?? t('epdWizard.messages.errorOnSaving')}</ErrorUpdating>
      )}

      <ToolButtons
        epd={epd}
        companyId={companyId || ''}
        updateEpd={updateEpd}
        agreementConsent={agreementConsent}
        validationStep1={step1ValidationSchema(epd?.verificationType, verifiers)}
      />

      <StatusBox epdState={epd.epdState} />

      <Wizard>
        <div>
          {wizardTabs.map((tab, index) => {
            const isTabValid = tab.isValid(epd);

            return (
              <WizardStep
                key={index}
                status={index === activeStep ? StepStatus.Active : StepStatus.Todo}
                onClick={() => setActiveStep(index)}
              >
                <div>{tab.name}</div>
                {isTabValid && (
                  <div>
                    <img src={Tick} width="16" height="16" alt="Step completed" />
                  </div>
                )}
                {isTabValid === false && <MandatoryIcon />}
              </WizardStep>
            );
          })}
        </div>
        <WizardContent>
          {wizardTabs.map(
            (tab, index) =>
              index === activeStep &&
              React.cloneElement(tab.component, {
                epd: epd,
                onChangePcr: changePcr,
                onChangeCpcr: changeCpcr,
                onRemovePcr: removePcr,
                onRemoveCpcr: removeCpcr,
                onChangeEpd: onChangeEpd,
                setUpdatedEpd: setEpd,
                goToStep: goToStep,
              })
          )}
        </WizardContent>
      </Wizard>
    </Container>
  );
};

export const AccordionContent = styled.div`
  border-left: solid 3px ${(props) => props.theme.colors.regionColorLight};
  padding: 1rem;
  display: flex;
`;

const Wizard = styled.section`
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr 3fr;
  width: 100%;
`;

const WizardStep = styled.div<{ status?: StepStatus }>`
  ${(props) => props.theme.fonts.heading3}
  display: flex;
  padding: 1rem;
  border-bottom: solid 1px ${(props) => props.theme.colors.gray};
  border-right: solid 3px ${(props) => props.theme.colors.gray};
  justify-content: space-between;

  ${(props) =>
    props.status === StepStatus.Active
      ? css`
          border-right-color: ${(props) => props.theme.colors.orange};
          background-color: ${(props) => props.theme.colors.lightGray};
        `
      : null};

  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.lightGray};
  }
`;

const WizardContent = styled.div`
  box-sizing: border-box;
  width: 100%;
`;

const ErrorUpdating = styled(ErrorText)`
  position: fixed;
  right: 0;
  margin: 1rem;
  padding: 1rem;
  border: solid 1px red;
  background: white;
  z-index: 1;
`;

export const Text = styled.div`
  margin-bottom: 1rem;
  margin-top: 2rem;
  text-align: left;
  ${(props) => props.theme.fonts.text}
  font-size: 0.96rem;
`;

export const ListBox = styled.div`
  border: solid 1px gray;
  margin-bottom: 0.5rem;
  height: 100px;
  overflow-y: scroll;
`;

export const ListBoxNoItems = styled.em`
  padding: 0.25rem 0.5rem;
  display: block;
`;

export default EpdWizard;
